import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { Client, Message, Paginator } from "@twilio/conversations";
import { Box, Spinner } from "@twilio-paste/core";
import InfiniteScroll from "react-infinite-scroll-component";

import MessageList from "./MessageList";
import { AddMessagesType } from "../../types";
import styles from "../../styles";
import { getMessages } from "../../api/api";
import { CONVERSATION_PAGE_SIZE } from "../../constants";
import { ReduxConversation } from "../../store/reducers/convoReducer";
import { getSdkConversationObject } from "../../conversations-objects";
import { ReduxMessage } from "../../store/reducers/messageListReducer";
import { ReduxParticipant } from "../../store/reducers/participantsReducer";

export async function loadMessages(
  conversation: ReduxConversation,
  upsertMessage: AddMessagesType,
  currentMessages: ReduxMessage[] = []
): Promise<void> {
  const convoSid: string = conversation.sid;
  if (currentMessages.length > 0) return;
  console.log("LOAD MESSAGE", convoSid);
  const paginator = await getMessages(getSdkConversationObject(conversation));
  upsertMessage(convoSid, paginator.items);
}

interface MessageProps {
  convoSid: string;
  client?: Client;
  convo: ReduxConversation;
  upsertMessage: AddMessagesType;
  messages: ReduxMessage[];
  loadingState: boolean;
  participants: ReduxParticipant[];
  lastReadIndex: number;
  use24hTimeFormat: boolean;
  handleDroppedFiles: (droppedFiles: File[]) => void;
  templateOnly: boolean;
}

const MessagesBox: React.FC<MessageProps> = (props) => {
  const {
    messages,
    convo,
    loadingState,
    lastReadIndex,
    upsertMessage,
    use24hTimeFormat,
    handleDroppedFiles,
    templateOnly,
  } = props;

  const [hasMore, setHasMore] = useState(
    (messages?.length ?? 0) === CONVERSATION_PAGE_SIZE
  );
  const [loading, setLoading] = useState(false);
  const [height, setHeight] = useState(0);
  const [paginator, setPaginator] = useState<Paginator<Message> | null>(null);

  const listRef = useRef<HTMLDivElement>(null);
  const isMountedRef = useRef(true);
  const timeoutRef = useRef<number | null>(null);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (timeoutRef.current) window.clearTimeout(timeoutRef.current);
    };
  }, []);

  const sdkConvo = useMemo(() => getSdkConversationObject(convo), [convo.sid]);

  useEffect(() => {
    if (!loadingState && convo?.sid && (messages?.length ?? 0) === 0) {
      loadMessages(convo, upsertMessage, messages);
    }
  }, [loadingState, convo.sid, upsertMessage, messages]);

  useLayoutEffect(() => {
    const currentHeight = listRef.current?.clientHeight ?? 0;
    if (currentHeight > height && loading) {
      if (timeoutRef.current) window.clearTimeout(timeoutRef.current);

      timeoutRef.current = window.setTimeout(() => {
        if (!isMountedRef.current) return;
        setHeight(currentHeight);
        setLoading(false);
      }, 2000);
    }
    return () => {
      if (timeoutRef.current) window.clearTimeout(timeoutRef.current);
    };
  }, [loading, height, messages?.length]);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const p = await getMessages(sdkConvo);
        if (cancelled || !isMountedRef.current) return;
        setHasMore(p.hasPrevPage);
        setPaginator(p);
      } catch (e) {}
    })();

    return () => {
      cancelled = true;
    };
  }, [sdkConvo]);

  useEffect(() => {
    const last = messages?.[messages.length - 1];
    if (last && last.index !== -1) {
      sdkConvo.advanceLastReadMessageIndex(last.index);
    }
  }, [messages, sdkConvo]);

  const lastConversationReadIndex = useMemo(() => {
    const last = messages?.[messages.length - 1];
    return last && last.author !== localStorage.getItem("username")
      ? lastReadIndex
      : -1;
  }, [lastReadIndex, messages]);

  const fetchMore = useCallback(async () => {
    if (!paginator) return;

    try {
      if (isMountedRef.current) setLoading(true);

      const result = await paginator.prevPage();
      if (!result || !isMountedRef.current) return;

      const moreMessages = result.items;
      setPaginator(result);
      setHasMore(result.hasPrevPage);
      upsertMessage(convo.sid, moreMessages);
    } finally {
      if (isMountedRef.current) setLoading(false);
    }
  }, [paginator, upsertMessage, convo.sid]);

  return (
    <Box
      key={convo.sid}
      id="scrollable"
      paddingRight="space50"
      style={{
        display: "flex",
        flexDirection: "column-reverse",
        width: "100%",
        overflowY: "auto",
        paddingLeft: 16,
        height: "100%",
      }}
    >
      <InfiniteScroll
        dataLength={messages?.length ?? 0}
        next={fetchMore}
        hasMore={!loading && hasMore}
        loader={
          <div style={styles.paginationSpinner}>
            <Spinner decorative={false} size="sizeIcon50" title="Loading" />
          </div>
        }
        scrollableTarget="scrollable"
        style={{
          display: "flex",
          overflow: "hidden",
          flexDirection: "column-reverse",
        }}
        inverse={true}
        scrollThreshold="20px"
      >
        <div ref={listRef}>
          <MessageList
            messages={messages ?? []}
            conversation={convo}
            participants={props.participants}
            lastReadIndex={lastConversationReadIndex}
            use24hTimeFormat={use24hTimeFormat}
            handleDroppedFiles={handleDroppedFiles}
          />
        </div>
      </InfiniteScroll>
    </Box>
  );
};

export default MessagesBox;
